import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Copy, Forms, Loading, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import { apiGet } from 'services';
import WidgetBase, { groupSet, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import PeriodPicker from 'components/admin/forms/periodPicker';
import { periodName, sortGoalsByPeriod } from 'helpers/goals';
import CompetencyRecommendations from '../components/competencyRecommendations';
import GoalPicker from 'components/admin/forms/goalPicker';

/*
Only change method name, system text, response mapping, validator and filter fields when copying
*/

export const GoalsAchieved = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [metric, setMetric] = React.useState({});
    const [recommendationData, setRecommendationData] = React.useState({});
    const [periodOptions, setPeriodOptions] = React.useState([]);

    const goals = useSelector(state => state.dashboard.goals);
    const metrics = useSelector(state => state.dashboard.metrics);
    const mappedGoals = sortGoalsByPeriod(goals, true);

    const defaults = { show_For: true, period: 'Q1' };

    const updatePeriodOptions = () => {
        if (values?.quota_Id) {
            let goal = goals.find(g => g.id === values.quota_Id);
            const options = [goal?.period];
            if (!options.some(o => values.period.charAt(0) === o)) {
                updateProperty(`${goal?.period}1`, 'period');
            }

            setPeriodOptions(options);
        }
    };

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    React.useEffect(() => {
        updatePeriodOptions();
        if (values?.quota_Id && ArrayHelpers.existsAndHasLength(goals) && ArrayHelpers.existsAndHasLength(metrics)) {
            const g = goals.find(x => x.id === values?.quota_Id);
            const m = metrics.find(x => x.id === g?.metric_Id);
            if (m) setMetric(m);
        }
    }, [goals, metrics, values?.quota_Id, values.quota_Id, values?.period]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Picker/Error');
            validForm = false;
        }

        if (!form.quota_Id) {
            errors.quota_Id = SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Goal/Error');
            validForm = false;
        }

        if (!form.period) {
            errors.period = SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Period/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = ArrayHelpers.existsAndHasLength(data) ? (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <Chart data={data} item={item} metric={metric} id={`chart-${item.id}`} />
        </div>
    ) : (
        <div>{SystemText('Dashboard/Widgets/General/NoData')}</div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        return ArrayHelpers.existsAndHasLength(recommendationData?.comps) ? (
            <CompetencyRecommendations
                text={SystemText(
                    'Dashboard/Widgets/GoalsAcheived/Recommends/Strongest',
                    recommendationData?.best,
                    StringHelpers.formatMetric('P', recommendationData?.score)
                )}
                comps={recommendationData?.comps}
            />
        ) : recommendationData?.best ? (
            <Copy
                value={SystemText(
                    'Dashboard/Widgets/GoalsAcheived/Recommends/Strongest/NoComps',
                    recommendationData?.best,
                    StringHelpers.formatMetric('P', recommendationData?.score)
                )}
            />
        ) : (
            <div>{SystemText('Dashboard/Widgets/Recommends/NoData')}</div>
        );
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                help={SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Picker/Help')}
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <GoalPicker
                label={SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Goal/Label')}
                help={SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Goal/Help')}
                mandatory
                data={mappedGoals}
                value={values.quota_Id}
                error={errors.quota_Id}
                onChange={value => updateProperty(value, 'quota_Id')}
            />
            {values.quota_Id && (
                <PeriodPicker
                    mandatory
                    limitTypes={periodOptions}
                    value={values.period}
                    error={errors.period}
                    onChange={value => updateProperty(value, 'period')}
                    help={SystemText('Dashboard/Widgets/GoalsAchieved/Fields/Period/Help')}
                />
            )}
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default GoalsAchieved;
